/**
 * Fields of each «Зафиксировать» event type (design QuickCapture.dc.html `renderVals`,
 * docs/research/design2-shell-capture.md §3.3): required ones, the optional section and its label.
 */
import { CURRENCIES, SEVERANCE_OPTIONS, SEVERANCE_RU, dateFullRu, dateShortRu, moneyRu } from '@outsource/shared';
import type { CaptureLookups } from '@outsource/shared';
import { currencyOf, listOf, payFormValues, rateFormatOf, realId } from './captureForm';
import type { FormState } from './captureForm';
import {
  ASSIGN_RATE_FORMATS,
  isShareFormat,
  RATE_LABEL,
  UNIT,
  anyItems,
  assignmentsOf,
  clientItems,
  invoiceOptions,
  invoicesOf,
  leaveEntityIds,
  personItems,
  projectItems,
} from './fieldItems';
import type { CaptureFieldSet, FieldSpec } from './fieldSpec';

export type { CaptureFieldSet, ComboItem, CreateKind, FieldSpec } from './fieldSpec';
function joinFields(state: FormState, l: CaptureLookups): CaptureFieldSet {
  return {
    required: [
      {
        kind: 'combo',
        key: 'person',
        label: 'Кто',
        required: true,
        create: 'person',
        placeholder: 'Имя человека',
        items: personItems(l, (p) => p.status !== 'left'),
      },
      {
        kind: 'combo',
        key: 'project',
        label: 'Какой проект',
        required: true,
        create: 'project',
        placeholder: 'Проект или клиент',
        items: projectItems(l, (p) => p.status !== 'ended'),
      },
      { kind: 'date', key: 'from', label: 'С какой даты', required: true },
      ...projectPayFields(state, l, realId(state.values.person)),
    ],
    optional: [
      { kind: 'text', key: 'plan', label: 'План, ч/мес', placeholder: '168', hint: 'Пусто = 168 ч' },
    ],
    optionalLabel: 'План часов',
  };
}

function leaveFields(state: FormState, l: CaptureLookups): CaptureFieldSet {
  const personId = realId(state.values.person);
  const running = new Set(l.assignments.filter((a) => !a.endedOn).map((a) => a.personId));
  const pickPerson = (id: string) => {
    const own = assignmentsOf(l, id);
    return { assignment: own.length === 1 && own[0] ? own[0].id : '' };
  };
  return {
    required: [
      {
        kind: 'combo',
        key: 'person',
        label: 'Кто',
        required: true,
        items: personItems(l, (p) => running.has(p.id)),
        onPick: pickPerson,
      },
      {
        kind: 'select',
        key: 'assignment',
        label: 'С какого проекта',
        required: true,
        options: [
          { value: '', label: personId ? 'Выберите проект' : 'Сначала человек' },
          ...assignmentsOf(l, personId).map((a) => ({ value: a.id, label: a.label })),
        ],
      },
      { kind: 'date', key: 'to', label: 'Последний день', required: true },
    ],
    optional: [],
    optionalLabel: '',
  };
}

function projectFields(state: FormState, l: CaptureLookups): CaptureFieldSet {
  const v = state.values;
  const clientId = realId(v.client);
  const contracts = clientId ? l.contracts.filter((k) => k.clientId === clientId) : [];
  const single =
    contracts.length === 1 && contracts[0] ? `подставится ${contracts[0].number}` : 'уточню позже';
  return {
    required: [
      {
        kind: 'text',
        key: 'name',
        label: 'Название',
        required: true,
        placeholder: 'Как на счёте, например «Mobile App Development»',
      },
      { kind: 'date', key: 'from', label: 'С какой даты', required: true },
    ],
    optional: [
      {
        kind: 'combo',
        key: 'client',
        label: 'Клиент',
        create: 'client',
        placeholder: 'Клиент',
        items: clientItems(l),
      },
      {
        kind: 'select',
        key: 'contract',
        label: 'Договор',
        options: [{ value: '', label: single }, ...contracts.map((k) => ({ value: k.id, label: k.number }))],
      },
      {
        kind: 'seg',
        key: 'billing',
        label: 'Оплата клиентом',
        options: [
          ['hourly', 'В час'],
          ['fixed', 'Фикс в месяц'],
        ],
      },
      {
        kind: 'money',
        key: 'rateV',
        label: v.billing === 'fixed' ? 'Фикс клиенту' : 'Ставка клиенту',
        currency: currencyOf('rateV', state, l),
      },
      {
        kind: 'combo',
        key: 'person',
        label: 'Кто на нём',
        items: personItems(l, (p) => p.status !== 'left'),
      },
    ],
    optionalLabel: 'Клиент · договор · ставка · люди',
  };
}

/** «Проект» or «Юрлицо» of «Изменение ставки человека» (V259): the own pay on a project, or the оклад. */
const RATE_WHERE: FieldSpec = {
  kind: 'seg',
  key: 'where',
  label: 'Где',
  options: [
    ['project', 'Проект'],
    ['entity', 'Юрлицо'],
  ],
};

function rateTargetFields(state: FormState, l: CaptureLookups): FieldSpec[] {
  const v = state.values;
  if (v.tkind === 'project') {
    return [
      {
        kind: 'combo',
        key: 'target',
        label: 'Проект',
        required: true,
        items: projectItems(l, (p) => p.billingMode !== 'internal'),
      },
    ];
  }
  const person: FieldSpec = {
    kind: 'combo',
    key: 'target',
    label: 'Человек',
    required: true,
    items: personItems(l, (p) => p.status !== 'left' || assignmentsOf(l, p.id, true).length > 0),
  };
  if (v.where === 'entity') return [person, RATE_WHERE, ...salaryEntityFields(state, l, realId(v.target))];
  const options = [
    { value: '', label: 'Выберите проект' },
    ...assignmentsOf(l, realId(v.target), true).map((a) => ({ value: a.id, label: a.endedOn ? `${a.label} · завершён ${dateShortRu(a.endedOn)}` : a.label })),
  ];
  const hint = 'Только для этого проекта. Например, сниженная ставка на время передачи дел';
  // Picking a project fills in its conditions in force (V255).
  const onPick = (assignmentId: string): Record<string, string> => {
    const a = l.assignments.find((x) => x.id === assignmentId);
    return payFormValues(a?.pay ?? null, a?.creditEntityIds ?? []);
  };
  return [person, RATE_WHERE, { kind: 'select', key: 'assignment', label: 'Назначение', required: true, options, hint, onPick }];
}

/** «оклад 1 000,00 BYN, на руки 860,00 BYN с 01.08.2026 — по договору 3»: the salary in force through the entity. */
function currentSalaryHint(l: CaptureLookups, personId: string | null, issuerId: string | undefined): string | undefined {
  const salary = l.people.find((p) => p.id === personId)?.salaries.find((s) => s.issuerId === issuerId);
  if (!salary) return issuerId ? 'Зарплаты через это юрлицо пока нет' : undefined;
  const net = moneyRu(salary.netMinor, salary.currency);
  const amount = salary.grossMinor == null ? `${net} на руки` : `оклад ${moneyRu(salary.grossMinor, salary.currency)}, на руки ${net}`;
  const source = salary.contractNumber ? ` — по договору ${salary.contractNumber}` : '';
  return `Сейчас ${amount} с ${dateFullRu(salary.from)}${source}`;
}

/**
 * «Юрлицо» in «Изменение ставки человека» (V259): the entity the person is employed in, with the
 * salary in force there, and the new оклад (brutto per month; on hand follows the entity's taxes).
 */
function salaryEntityFields(state: FormState, l: CaptureLookups, personId: string | null): FieldSpec[] {
  const entities = personEntities(l, personId);
  const options = [{ value: '', label: entities.length > 0 ? 'Выберите юрлицо' : 'Человек не оформлен в юрлицо' }, ...entities.map((e) => ({ value: e.id, label: e.name }))];
  const hint = currentSalaryHint(l, personId, state.values.entity);
  return [
    { kind: 'select', key: 'entity', label: 'Юрлицо', required: true, options, ...(hint ? { hint } : {}) },
    { kind: 'money', key: 'salaryV', label: 'Оклад (брутто)', required: true, currency: currencyOf('salaryV', state, l), unit: UNIT.monthly },
  ];
}

/**
 * «Зачесть зарплату через юрлицо» (V255): with an own rate on the project, the ticked entities'
 * salary is credited against the project's pay (V145) — the pay is reduced by it and the rest is paid
 * directly. Not offered for the «Зарплата через юрлицо» format (the whole pay goes through the entity).
 */
function creditFields(state: FormState, l: CaptureLookups, personId: string | null, format: string): FieldSpec[] {
  const person = l.people.find((p) => p.id === personId);
  if (!person || person.salaries.length === 0 || format === 'salary') return [];
  const options = person.salaries.flatMap((salary) => {
    const entity = l.entities.find((e) => e.id === salary.issuerId);
    return entity ? [{ value: entity.id, label: entity.name, note: `${moneyRu(salary.netMinor, salary.currency)} на руки` }] : [];
  });
  const picked = listOf(state.values.credit);
  const hint = picked.length > 0 ? 'Зарплата через юрлицо списывает оплату по проекту; остаток — к выплате напрямую' : 'Не отмечено — выплата по проекту идёт полностью, отдельно от зарплаты';
  return [{ kind: 'checks', key: 'credit', label: 'Зачесть зарплату через юрлицо', options, empty: '—', hint }];
}

/** The entities a person is employed in (V202): the only ones «Зарплата через юрлицо» offers. */
const personEntities = (l: CaptureLookups, personId: string | null) => {
  const ids = l.people.find((p) => p.id === personId)?.entityIds ?? [];
  return l.entities.filter((e) => ids.includes(e.id));
};

/**
 * The salary through the chosen entity, read-only on a project (V238): it is set by the employment
 * contract or on «Оформление», the project takes only its percent.
 */
function salaryHint(l: CaptureLookups, personId: string | null, issuerId: string | undefined): string | undefined {
  const salary = l.people.find((p) => p.id === personId)?.salaries.find((s) => s.issuerId === issuerId);
  if (!salary) return undefined;
  const net = moneyRu(salary.netMinor, salary.currency);
  const amount = salary.grossMinor == null ? `${net} на руки` : `оклад ${moneyRu(salary.grossMinor, salary.currency)}, на руки ${net}`;
  const source = salary.contractNumber ? `по договору ${salary.contractNumber}` : 'при оформлении';
  return `${amount} с ${dateFullRu(salary.from)} — задано ${source}, на проекте не меняется`;
}

/**
 * A person's pay on a project (V169, V202): every format; «Зарплата через юрлицо» only while they are
 * employed in an entity, and only those entities, with their salary shown read-only (V238); a
 * percent or the salary needs no currency.
 */
export function projectPayFields(state: FormState, l: CaptureLookups, personId: string | null): FieldSpec[] {
  const entities = personEntities(l, personId);
  const options = ASSIGN_RATE_FORMATS.filter((f) => f.value !== 'salary' || entities.length > 0);
  const format = state.values.rateFmt || 'hourly';
  const hint = salaryHint(l, personId, state.values.issuer);
  const values = rateValueFields(state, l, format, entities).map((field) => (field.key === 'issuer' && hint ? { ...field, hint } : field));
  return [{ kind: 'select', key: 'rateFmt', label: 'Тип ставки', options }, ...values];
}

/** The value of the new rate: an amount, a percent, or the entity of the salary (V169). */
function rateValueFields(state: FormState, l: CaptureLookups, format: string, salaryEntities: readonly { readonly id: string; readonly name: string }[]): FieldSpec[] {
  if (isShareFormat(format)) return [{ kind: 'text', key: 'share', label: 'Процент', required: true, placeholder: 'например 10' }];
  if (format === 'salary') {
    const entities = [{ value: '', label: 'Выберите юрлицо' }, ...salaryEntities.map((e) => ({ value: e.id, label: e.name }))];
    return [
      { kind: 'select', key: 'issuer', label: 'Юрлицо', required: true, options: entities },
      { kind: 'text', key: 'share', label: '% зарплаты', placeholder: 'сколько зарплаты идёт по проекту' },
    ];
  }
  const money: FieldSpec = {
    kind: 'money',
    key: 'rateV',
    label: RATE_LABEL[format] ?? 'Ставка',
    required: true,
    currency: currencyOf('rateV', state, l),
    unit: UNIT[format],
  };
  return [money];
}

function rateFields(state: FormState, l: CaptureLookups): CaptureFieldSet {
  const v = state.values;
  // Whose rate it is comes from the action picked (V169): a project's price to the client, or a
  // person's pay on a project with the same fields as «Выходит на проект» (V202).
  const format = rateFormatOf(state, l);
  const value: FieldSpec[] =
    v.tkind === 'project'
      ? [{ kind: 'money', key: 'rateV', label: 'Новая ставка', required: true, currency: currencyOf('rateV', state, l) }]
      : v.where === 'entity'
        ? []
        : [...projectPayFields({ ...state, values: { ...v, rateFmt: format } }, l, realId(v.target)), ...creditFields(state, l, realId(v.target), format)];
  return {
    required: [...rateTargetFields(state, l), ...value, { kind: 'date', key: 'from', label: 'С какой даты', required: true }],
    optional: [],
    optionalLabel: '',
  };
}

/**
 * «Новый человек / условия» (V202, V245): who; contractor or employee and the entity they are employed
 * in; optionally a project with their rate on it.
 */
function personFields(state: FormState, l: CaptureLookups): CaptureFieldSet {
  const v = state.values;
  const entities = [{ value: '', label: 'Без юрлица' }, ...l.entities.map((e) => ({ value: e.id, label: e.name }))];
  const project: FieldSpec[] = realId(v.project)
    ? [...projectPayFields(state, l, realId(v.person)), { kind: 'date', key: 'from', label: 'На проекте с даты', required: true }]
    : [];
  return {
    required: [
      {
        kind: 'combo',
        key: 'person',
        label: 'Кто',
        required: true,
        create: 'person',
        placeholder: 'Новое имя или существующий человек',
        items: personItems(l),
      },
    ],
    optional: [
      {
        kind: 'seg',
        key: 'kind',
        label: 'Оформление',
        options: [
          ['contractor', 'Подрядчик'],
          ['employee', 'Сотрудник'],
        ],
      },
      { kind: 'select', key: 'entity', label: 'Юрлицо', options: entities },
      { kind: 'combo', key: 'project', label: 'Проект', placeholder: 'Сразу на проект', items: projectItems(l, (p) => p.status !== 'ended') },
      ...project,
    ],
    optionalLabel: 'Оформление · проект',
  };
}

function statusFields(_state: FormState, l: CaptureLookups): CaptureFieldSet {
  return {
    required: [
      { kind: 'combo', key: 'project', label: 'Проект', required: true, items: projectItems(l) },
      {
        kind: 'seg',
        key: 'status',
        label: 'Что случилось',
        options: [
          ['paused', 'Пауза'],
          ['ended', 'Завершён'],
          ['active', 'Возобновлён'],
        ],
      },
      { kind: 'date', key: 'date', label: 'С какой даты', required: true },
    ],
    optional: [],
    optionalLabel: '',
  };
}

/** The currencies follow the picked document (V187): typed ones are dropped on a new pick. */
const RESET_CURRENCIES = { amountCur: '', feeCur: '' } as const;

/** «Клиент оплатил» pays one document (V187); a client with a single open one gets it picked. */
function paymentFields(state: FormState, l: CaptureLookups): CaptureFieldSet {
  const clientId = realId(state.values.client);
  const invoice = l.invoices.find((i) => i.id === state.values.invoice);
  const pickClient = (id: string) => {
    const open = invoicesOf(l, id);
    return { ...RESET_CURRENCIES, invoice: open.length === 1 && open[0] ? open[0].id : '' };
  };
  return {
    required: [
      { kind: 'combo', key: 'client', label: 'Клиент', required: true, items: clientItems(l), onPick: pickClient },
      {
        kind: 'select',
        key: 'invoice',
        label: 'Счёт',
        required: true,
        options: invoiceOptions(l, clientId),
        onPick: () => RESET_CURRENCIES,
      },
      {
        kind: 'money',
        key: 'amount',
        label: 'Сумма',
        required: true,
        currency: currencyOf('amount', state, l),
        ...(invoice ? { hint: `Открыто ${moneyRu(invoice.openMinor, invoice.currency)}` } : {}),
      },
      { kind: 'date', key: 'date', label: 'Дата поступления', required: true },
    ],
    optional: [{ kind: 'money', key: 'fee', label: 'Комиссия банка', currency: currencyOf('fee', state, l) }],
    optionalLabel: 'Комиссия банка',
  };
}

/** «Данные человека» (V265, V266): picking the person fills in the name and the location to edit. */
function editFields(_state: FormState, l: CaptureLookups): CaptureFieldSet {
  const pickPerson = (id: string): Record<string, string> => {
    const person = l.people.find((p) => p.id === id);
    return { name: person?.name ?? '', location: person?.location ?? '' };
  };
  return {
    required: [
      { kind: 'combo', key: 'person', label: 'Кто', required: true, items: personItems(l), onPick: pickPerson },
      { kind: 'text', key: 'name', label: 'Имя', required: true },
      { kind: 'text', key: 'location', label: 'Локация', placeholder: 'Город, страна' },
    ],
    optional: [],
    optionalLabel: '',
  };
}

function noteFields(_state: FormState, l: CaptureLookups): CaptureFieldSet {
  return {
    required: [
      {
        kind: 'area',
        key: 'text',
        label: 'Что уточнить',
        required: true,
        placeholder: 'Например: узнать у Kalvik, продлевают ли договор',
      },
    ],
    optional: [{ kind: 'combo', key: 'ref', label: 'К чему относится', items: anyItems(l) }],
    optionalLabel: 'К чему относится',
  };
}

/**
 * «Увольнение из юрлица» (V178, V292): who, from which entity — the person's own one picked with them —
 * the last working day and the severance.
 */
function dismissFields(_state: FormState, l: CaptureLookups): CaptureFieldSet {
  const entities = [{ value: '', label: 'Выберите юрлицо' }, ...l.entities.map((e) => ({ value: e.id, label: e.name }))];
  const pickPerson = (id: string): Record<string, string> => ({ entity: l.people.find((p) => p.id === id)?.employerId ?? '' });
  const severance = SEVERANCE_OPTIONS.map((value) => ({ value, label: SEVERANCE_RU[value] }));
  return {
    required: [
      { kind: 'combo', key: 'person', label: 'Кто', required: true, items: personItems(l, (p) => p.status !== 'left'), onPick: pickPerson },
      { kind: 'select', key: 'entity', label: 'Из какого юрлица', required: true, options: entities },
      { kind: 'date', key: 'lastDay', label: 'Последний рабочий день', required: true },
      { kind: 'select', key: 'severance', label: 'Выходное пособие', required: true, options: severance },
    ],
    optional: [],
    optionalLabel: '',
  };
}

/**
 * «Оформление в юрлицо» (V203): who, the entity, from when, and the salary through it. The entities
 * the person is already employed in are not offered (V237); picking a person clears the entity.
 */
function employFields(state: FormState, l: CaptureLookups): CaptureFieldSet {
  const employed = l.people.find((p) => p.id === realId(state.values.person))?.entityIds ?? [];
  const open = l.entities.filter((e) => !employed.includes(e.id));
  const first = open.length > 0 ? 'Выберите юрлицо' : 'Уже оформлен во всех юрлицах';
  const entities = [{ value: '', label: first }, ...open.map((e) => ({ value: e.id, label: e.name }))];
  const pickPerson = (): Record<string, string> => ({ entity: '' });
  return {
    required: [
      { kind: 'combo', key: 'person', label: 'Кто', required: true, items: personItems(l, (p) => p.status !== 'left'), onPick: pickPerson },
      { kind: 'select', key: 'entity', label: 'Юрлицо', required: true, options: entities },
      { kind: 'date', key: 'from', label: 'С какой даты', required: true },
      { kind: 'money', key: 'netV', label: 'Зарплата на руки', unit: 'в месяц', currency: currencyOf('netV', state, l) },
    ],
    optional: [{ kind: 'money', key: 'advanceV', label: 'Из них аванс', currency: currencyOf('netV', state, l) }],
    optionalLabel: 'Аванс',
  };
}

/**
 * «Отпуск / за свой счёт» (V233–V235): which of the two, who — anybody, a contractor too — the projects
 * the leave is on (their plan loses its workdays) and the entities whose salary pays for those
 * projects — «на ставке», vacation pay follows the entity's labour rules. Picking a person ticks their
 * running projects and those entities.
 */
function absenceFields(state: FormState, l: CaptureLookups): CaptureFieldSet {
  const personId = realId(state.values.person);
  const salaryIds = l.people.find((p) => p.id === personId)?.salaryEntityIds ?? [];
  const applicable = leaveEntityIds(l, listOf(state.values.assignments));
  const pickPerson = (id: string): Record<string, string> => {
    const running = assignmentsOf(l, id).map((a) => a.id);
    return { assignments: running.join(','), entities: leaveEntityIds(l, running).join(',') };
  };
  return {
    required: [
      {
        kind: 'seg',
        key: 'kind',
        label: 'Что',
        options: [
          ['vacation', 'Отпуск'],
          ['unpaid', 'За свой счёт'],
        ],
      },
      { kind: 'combo', key: 'person', label: 'Кто', required: true, items: personItems(l, (p) => p.status !== 'left'), onPick: pickPerson },
      { kind: 'date', key: 'from', label: 'Первый день', required: true },
      { kind: 'date', key: 'to', label: 'Последний день', required: true },
      {
        kind: 'checks',
        key: 'assignments',
        label: 'Проекты',
        options: assignmentsOf(l, personId).map((a) => ({ value: a.id, label: a.label })),
        empty: personId ? 'Нет текущих проектов' : 'Сначала человек',
      },
      {
        kind: 'checks',
        key: 'entities',
        label: 'Юрлица',
        options: l.entities
          .filter((e) => applicable.includes(e.id))
          .map((e) => ({ value: e.id, label: e.name, ...(salaryIds.includes(e.id) ? { note: 'на ставке' } : {}) })),
        empty: personId ? 'Выбранные проекты не платятся через юрлицо' : 'Сначала человек',
      },
    ],
    optional: [],
    optionalLabel: '',
  };
}

/**
 * «Премия / денежная помощь» (V287): which of the two, who — someone on a salary through our entity —
 * the entity (picked with the person when they have one salary), the amount on hand in the salary's
 * currency and the date whose month gets the event.
 */
function extraFields(state: FormState, l: CaptureLookups): CaptureFieldSet {
  const personId = realId(state.values.person);
  const salaries = l.people.find((p) => p.id === personId)?.salaries ?? [];
  const entities = l.entities.filter((e) => salaries.some((s) => s.issuerId === e.id));
  const pickPerson = (id: string): Record<string, string> => {
    const own = l.people.find((p) => p.id === id)?.salaries ?? [];
    return { entity: own.length === 1 && own[0] ? own[0].issuerId : '', amountCur: '' };
  };
  const first = !personId ? 'Сначала человек' : entities.length > 0 ? 'Выберите юрлицо' : 'Нет зарплаты через юрлицо';
  return {
    required: [
      {
        kind: 'seg',
        key: 'kind',
        label: 'Что',
        options: [
          ['bonus', 'Премия'],
          ['aid', 'Денежная помощь молодому специалисту'],
        ],
      },
      { kind: 'combo', key: 'person', label: 'Кто', required: true, items: personItems(l, (p) => p.salaries.length > 0), onPick: pickPerson },
      { kind: 'select', key: 'entity', label: 'Юрлицо', required: true, options: [{ value: '', label: first }, ...entities.map((e) => ({ value: e.id, label: e.name }))] },
      { kind: 'money', key: 'amount', label: 'Сумма на руки', required: true, currency: currencyOf('amount', state, l) },
      { kind: 'date', key: 'date', label: 'Дата', required: true },
    ],
    optional: [],
    optionalLabel: '',
  };
}

/** «Новый клиент» (V181): the name, then the code name, country and currency to add later. */
function clientFields(_state: FormState, _l: CaptureLookups): CaptureFieldSet {
  return {
    required: [{ kind: 'text', key: 'name', label: 'Название', required: true, placeholder: 'Юридическое название, как в договоре' }],
    optional: [
      { kind: 'text', key: 'codeName', label: 'Кодовое название', placeholder: 'Как называем между собой' },
      { kind: 'text', key: 'country', label: 'Страна' },
      { kind: 'select', key: 'currency', label: 'Валюта', options: CURRENCIES.map((c) => ({ value: c, label: c })) },
    ],
    optionalLabel: 'Кодовое название · страна · валюта',
  };
}

const BUILDERS = {
  absence: absenceFields,
  client: clientFields,
  dismiss: dismissFields,
  employ: employFields,
  extra: extraFields,
  join: joinFields,
  leave: leaveFields,
  project: projectFields,
  rate: rateFields,
  person: personFields,
  status: statusFields,
  payment: paymentFields,
  note: noteFields,
  edit: editFields,
} as const;

const EMPTY: CaptureFieldSet = { required: [], optional: [], optionalLabel: '' };

export function captureFields(state: FormState, lookups: CaptureLookups): CaptureFieldSet {
  return state.type ? BUILDERS[state.type](state, lookups) : EMPTY;
}

/** Seg defaults do not open the optional section by themselves (design). */
const SEG_DEFAULT_KEYS = new Set(['billing', 'kind', 'format']);

export function showsOptional(state: FormState, set: CaptureFieldSet): boolean {
  return state.optional || set.optional.some((f) => !SEG_DEFAULT_KEYS.has(f.key) && !!state.values[f.key]);
}
